import React, { SyntheticEvent, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from 'app/store';
import { useParams } from 'react-router-dom';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import { Typography, Box, Button, Tab, Tabs, CircularProgress } from '@mui/material';
import { MoreInfo } from 'shared/ui/MoreInfo/MoreInfo';
import { useGetActivitiesByIdQuery } from 'features/getActivities/api/getActivitiesApi';
import { BonusesColumnType, ObjectivesType, ParticipantPromoId, PromoDetailStateType } from 'entities/promo/types';
import { useGetLanguagesQuery } from 'features/getLanguages/getLanguagesApi';
import { LanguageType, TabPanelTypeProps } from 'shared/types';
import { AboutWrapper, TableWrapper } from 'widgets/promoB2B/ui/PromoB2BDetailStyles';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { DataTableFrontendPagination } from 'shared/ui/Table';
import { useGetBonusesStatisticsQuery } from 'features/userTransactions/api';
import { fieldsBonusConfig, fieldsMechanicsConfig, fieldsParticipantsConfig } from 'widgets/promoB2B/model';
import { addDrawer, deletePromoDialog } from 'widgets/promoB2B/lib';
import { useGetParticipantsPromoIdQuery } from 'features/getParticipants/api/getParticipantsApi';

export const PromoB2BDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { isDrawerOpen } = useAppSelector((state) => state.ui);
  const [openDialog, setOpenDialog] = useState(false);
  const [state, setState] = useState<PromoDetailStateType>({
    value: 0,
    deleteVariant: '',
    drawerVariant: 'edit',
    title: 'Изменение данных',
    deletedId: 0,
  });

  const [searchParticipants, setSearchParticipants] = useState('');

  const { data: languagesList, isSuccess: isSuccessLanguagesList } = useGetLanguagesQuery(null);
  const { data: activityList, isSuccess: isSuccessActivity } = useGetActivitiesByIdQuery({ id });
  const { data: participants } = useGetParticipantsPromoIdQuery({
    id,
    search: searchParticipants,
  });

  const { data: bonuses } = useGetBonusesStatisticsQuery({ activityId: id });

  const transformedBonusesData =
    bonuses?.items.map((bonus) => ({
      ...bonus,
      uid: bonus.sales_point?.door_code,
      name: bonus.sales_point?.title,
    })) || [];

  const onSearch = debounce((search) => {
    setSearchParticipants(search);
  }, 300);

  useEffect(() => {
    if (!isDrawerOpen) {
      setState({
        ...state,
        drawerVariant: 'edit',
        title: 'Изменение данных',
      });
    }
  }, [isDrawerOpen]);

  function a11yProps(index: number) {
    const id = `simple-tab-${index}`;
    return { id, 'aria-controls': `simple-tabpanel-${index}` };
  }

  const TabPanel = ({ children, value, index, ...other }: TabPanelTypeProps) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ marginTop: '16px' }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setState({ ...state, value: newValue });
  };

  const dateFormat = (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm');

  const handleDrawerAdd = (variant: string, title: string) => {
    setState({
      ...state,
      drawerVariant: variant,
      title,
    });

    dispatch(setDrawerOpen(true));
  };

  const handleDrawerDelete = (id: number, variant: string) => {
    setState({ ...state, deleteVariant: variant, deletedId: id });
    setOpenDialog(true);
  };

  return (
    <Box>
      <Typography>Данные акции</Typography>
      {isSuccessActivity ? (
        <div>
          {isSuccessLanguagesList && (
            <div>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={state.value}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                >
                  {languagesList.map((language: LanguageType, index: number) => (
                    <Tab
                      key={language.codename}
                      label={language.codename}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>
              </Box>
              {languagesList.map((language: LanguageType, index: number) => (
                <TabPanel
                  key={language.codename}
                  value={state.value}
                  index={index}
                >
                  <AboutWrapper>
                    <div className="infoBlock">
                      <MoreInfo>
                        <div className="title">ID Акции:</div>
                        <div className="content">{activityList.id || '---'}</div>
                      </MoreInfo>
                      <MoreInfo>
                        <div className="title">Количество торговых точек:</div>
                        <div className="content">{activityList.participants.length || '---'}</div>
                      </MoreInfo>
                      <MoreInfo>
                        <div className="title">Период регистрации:</div>
                        <div className="content">
                          {`${dateFormat(activityList.reg_start_date)} - ${dateFormat(activityList.reg_end_date)}` ||
                            '---'}
                        </div>
                      </MoreInfo>
                      <MoreInfo>
                        <div className="title">Период активности:</div>
                        <div className="content">
                          {`${dateFormat(activityList.activity_start_date)} - ${dateFormat(
                            activityList.activity_end_date,
                          )}` || '---'}
                        </div>
                      </MoreInfo>
                      <MoreInfo>
                        <div className="title">Период доступа бонусов:</div>
                        <div className="content">
                          {`${dateFormat(activityList.bonus_start_date)} - ${dateFormat(
                            activityList.bonus_end_date,
                          )}` || '---'}
                        </div>
                      </MoreInfo>
                    </div>
                    <div className="infoBlock">
                      <MoreInfo>
                        <div className="title">Заголовок до участия:</div>
                        <div className="content">{activityList.title_before[`${language.codename}`] || '---'}</div>
                      </MoreInfo>
                      <MoreInfo>
                        <div className="title">Описание до участия:</div>
                        <div className="content">
                          {activityList.description_before[`${language.codename}`] || '---'}
                        </div>
                      </MoreInfo>
                    </div>
                    <div className="infoBlock">
                      <MoreInfo>
                        <div className="title">Название акции:</div>
                        <div className="content">{activityList.name[`${language.codename}`] || '---'}</div>
                      </MoreInfo>
                      <MoreInfo>
                        <div className="title">Описание:</div>
                        <div className="content">{activityList.description[`${language.codename}`] || '---'}</div>
                      </MoreInfo>
                    </div>
                  </AboutWrapper>
                </TabPanel>
              ))}
            </div>
          )}
          <TableWrapper>
            <Box textAlign="end">
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDrawerAdd('addMechanic', 'Добавить механику')}
              >
                Добавить механику
              </Button>
            </Box>
            <DataTableFrontendPagination<ObjectivesType>
              title="Добавить механику"
              data={activityList.objectives || []}
              columnConfig={fieldsMechanicsConfig}
              handleDrawerDelete={handleDrawerDelete}
              deleteIcon
              variantDrawer="deleteMechanic"
            />
          </TableWrapper>

          <TableWrapper>
            <Box textAlign="end">
              <Button
                style={{ marginRight: '10px' }}
                variant="contained"
                color="primary"
                onClick={() => handleDrawerAdd('uploadShopDots', 'Загрузить Файл')}
              >
                Загрузить Excel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDrawerAdd('addParticipant', 'Добавить торговую точку')}
              >
                Добавить торговую точку
              </Button>
            </Box>
            <DataTableFrontendPagination<ParticipantPromoId>
              title="Торговые точки"
              onSearch={onSearch}
              data={participants || []}
              columnConfig={fieldsParticipantsConfig}
              handleDrawerDelete={handleDrawerDelete}
              deleteIcon
              variantDrawer="deleteParticipant"
              search
            />
          </TableWrapper>

          <TableWrapper>
            <Box textAlign="end">
              <Button
                style={{ marginRight: '10px' }}
                variant="contained"
                color="primary"
                onClick={() => handleDrawerAdd('uploadBonus', 'Загрузить Файл')}
              >
                Загрузить Excel
              </Button>
            </Box>
            <DataTableFrontendPagination<BonusesColumnType>
              title="Бонусы"
              data={transformedBonusesData}
              columnConfig={fieldsBonusConfig}
            />
          </TableWrapper>
          {addDrawer(id!, state)}
          {deletePromoDialog(id!, state, openDialog, setOpenDialog)}
        </div>
      ) : (
        <Box
          width="100%"
          textAlign="center"
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};
